import { useState, useCallback } from 'react';
import { isValidPassword, type AccountRole } from '../../accounts';
import { PASSWORD_MIN_LENGTH } from '../../config/constants';
import { changePassword } from '../../data/supabase/auth';
interface UsePasswordChangeOptions {
  role: AccountRole;
  /**
   * Nog geaccepteerd omdat de aanroepers ze meegeven, maar niet meer gebruikt:
   * het wachtwoord staat bij Supabase en er is geen lokale hash meer om bij te
   * werken. Ze hier weghalen raakt elk profielscherm en hoort bij het
   * opschonen van de lokale domeinlaag.
   */
  accountId?: string;
  passwordHash?: string | undefined;
  updatePassword?: (accountId: string, newHash: string) => void;
}
interface UsePasswordChangeReturn {
  isChangingPassword: boolean;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  passwordError: string;
  passwordSuccess: string;
  setIsChangingPassword: (v: boolean) => void;
  setCurrentPassword: (v: string) => void;
  setNewPassword: (v: string) => void;
  setConfirmPassword: (v: string) => void;
  handleChangePassword: (e: React.FormEvent) => Promise<void>;
  resetPasswordForm: () => void;
}
export function usePasswordChange({
  role
}: UsePasswordChangeOptions): UsePasswordChangeReturn {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const resetPasswordForm = useCallback(() => {
    setIsChangingPassword(false);
    setPasswordError('');
    setPasswordSuccess('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, []);
  const handleChangePassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Vul alle velden in.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('De nieuwe wachtwoorden komen niet overeen.');
      return;
    }
    if (!isValidPassword(newPassword, role)) {
      const minLen = role === 'learner' ? PASSWORD_MIN_LENGTH.learner : PASSWORD_MIN_LENGTH.default;
      setPasswordError(`Het nieuwe wachtwoord moet minimaal ${minLen} tekens lang zijn.`);
      return;
    }
    // Het wachtwoord staat bij Supabase, niet in een lokale hash. Hieronder
    // stond een lokale tak die de hash in localStorage bijwerkte: die
    // "lukte" altijd, waarna de gebruiker gewoon met zijn oude wachtwoord
    // inlogde. Valse zekerheid is erger dan een foutmelding.
    const res = await changePassword(currentPassword, newPassword);
    if (!res.ok) {
      setPasswordError(res.message ?? 'Wijzigen is niet gelukt.');
      return;
    }
    setPasswordSuccess('Je wachtwoord is succesvol gewijzigd.');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => {
      setIsChangingPassword(false);
      setPasswordSuccess('');
    }, 3000);
  }, [currentPassword, newPassword, confirmPassword, role]);
  return {
    isChangingPassword,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordError,
    passwordSuccess,
    setIsChangingPassword,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    handleChangePassword,
    resetPasswordForm
  };
}